import { compact } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";

import AskAI from "@apps/ask-ai/ask-ai";
import useLabel from "@apps/use-label/use-label";
import WorkHistorySidebar from "@apps/work-history/components/work-history-sidebar";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import Callout from "@components/callout/callout";
import Layout from "@components/layout/layout";
import { ToggleButtonGroupType } from "@components/toggle-button-group/toggle-button-group";
import useDocumentTitle from "@components/use-document-title/use-document-title";

import ActionItems from "./components/action-items";
import Feedbacks from "./components/feedbacks";
import Goals from "./components/goals";
import Insights from "./components/insights";
import DashboardPeopleTable from "./components/people-table";
import Recognitions from "./components/recognitions";

enum DashboardSidebarTabEnum {
  items = "Items",
  askAI = "Topicflow AI",
  workHistory = "Work History",
}

const Dashboard = () => {
  const [askAIChatSessionId, setAskAIChatSessionId] = useState<null | number>(
    null,
  );
  const currentOrganization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const [sidebarActiveTab, setSidebarActiveTab] = useState(
    DashboardSidebarTabEnum.items,
  );
  const [startedChatSession, setStartedChatSession] = useState(false);

  useDocumentTitle("");

  const handleChangeSidebarTab = (
    option: ToggleButtonGroupType<DashboardSidebarTabEnum>,
  ) => {
    if (option.value) setSidebarActiveTab(option.value);
  };

  const showSidebarItemsTab =
    currentOrganization.featureFlags.actionItems ||
    currentOrganization.featureFlags.goals ||
    currentOrganization.featureFlags.recognitions ||
    currentOrganization.featureFlags.feedbacks;

  const sidebarTabs = compact([
    showSidebarItemsTab && {
      title: DashboardSidebarTabEnum.items,
      value: DashboardSidebarTabEnum.items,
      active: DashboardSidebarTabEnum.items === sidebarActiveTab,
    },
    currentOrganization.featureFlags.askAi && {
      title: DashboardSidebarTabEnum.askAI,
      value: DashboardSidebarTabEnum.askAI,
      active: DashboardSidebarTabEnum.askAI === sidebarActiveTab,
    },
    currentOrganization.featureFlags.workHistory && {
      title: DashboardSidebarTabEnum.workHistory,
      value: DashboardSidebarTabEnum.workHistory,
      active: DashboardSidebarTabEnum.workHistory === sidebarActiveTab,
    },
  ]);

  const sidebarExpandedUiPreferenceKey =
    sidebarTabs.length > 0 ? "dashboardSidebarExpanded" : undefined;

  const insights = Insights({ selectedUser: currentUser });
  const label = useLabel();

  useEffect(() => {
    if (sidebarTabs.length > 0) {
      const activeTab = sidebarTabs.find((tab) => tab.active);
      if (!activeTab) {
        setSidebarActiveTab(sidebarTabs[0].value);
      }
    }
  }, [sidebarTabs]);

  return (
    <Layout>
      <Layout.Header
        sidebarExpandedUiPreferenceKey={sidebarExpandedUiPreferenceKey}
        title=""
      />
      <Layout.Container>
        <>
          <Layout.Main
            width="default"
            sidebarExpandedUiPreferenceKey={sidebarExpandedUiPreferenceKey}
          >
            {insights === null &&
            !currentOrganization.featureFlags.homepageMyConnections ? (
              <div className="mx-auto max-w-(--breakpoint-md)">
                <Callout
                  className="mt-8"
                  icon={"info"}
                  center
                  title="No Content Available"
                >
                  Once {label("review", { pluralize: true })} begin, you'll see
                  key requirements, insights from peers and managers, and any
                  requests that need your attention.
                </Callout>
              </div>
            ) : (
              <>
                {insights}
                {currentOrganization.featureFlags.homepageMyConnections && (
                  <DashboardPeopleTable selectedUser={currentUser} />
                )}
              </>
            )}
          </Layout.Main>
          {sidebarTabs.length > 0 && (
            <Layout.Sidebar<DashboardSidebarTabEnum>
              sidebarExpandedUiPreferenceKey={sidebarExpandedUiPreferenceKey}
              fullHeight={DashboardSidebarTabEnum.askAI === sidebarActiveTab}
              tabs={sidebarTabs}
              onChangeTab={handleChangeSidebarTab}
            >
              {showSidebarItemsTab &&
                sidebarActiveTab === DashboardSidebarTabEnum.items &&
                currentOrganization.featureFlags.actionItems && (
                  <ActionItems selectedUser={currentUser} />
                )}
              {showSidebarItemsTab &&
                sidebarActiveTab === DashboardSidebarTabEnum.items &&
                currentOrganization.featureFlags.goals && (
                  <Goals selectedUser={currentUser} />
                )}
              {showSidebarItemsTab &&
                sidebarActiveTab === DashboardSidebarTabEnum.items &&
                currentOrganization.featureFlags.recognitions && (
                  <Recognitions selectedUser={currentUser} />
                )}
              {showSidebarItemsTab &&
                sidebarActiveTab === DashboardSidebarTabEnum.items &&
                currentOrganization.featureFlags.feedbacks && (
                  <Feedbacks selectedUser={currentUser} />
                )}
              {sidebarActiveTab === DashboardSidebarTabEnum.askAI && (
                <AskAI
                  isInSidebar={true}
                  chatSessionId={askAIChatSessionId}
                  onChangeChatSessionId={setAskAIChatSessionId}
                  isFullScreen={!!askAIChatSessionId || startedChatSession}
                  className="px-6 pb-6"
                  totalYPaddingAroundContainer={110}
                  onStartChatSession={() => {
                    setStartedChatSession(true);
                  }}
                />
              )}
              {sidebarActiveTab === DashboardSidebarTabEnum.workHistory && (
                <WorkHistorySidebar
                  userId={currentUser.id}
                  startDate={moment().subtract(7, "days").format("YYYY-MM-DD")}
                  endDate={moment().format("YYYY-MM-DD")}
                  hasViewingPermission
                />
              )}
            </Layout.Sidebar>
          )}
        </>
      </Layout.Container>
    </Layout>
  );
};

export default Dashboard;
